import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const TOKEN_KEY = "bbk_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const refreshRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) { setUser(null); setReady(true); return null; }
    try {
      const { data } = await endpoints.me();
      setUser(data);
      return data;
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401 || st === 403) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      } else {
        // Server briefly unreachable (5xx/520/network): keep the member signed in and re-check soon.
        setTimeout(() => { refreshRef.current && refreshRef.current(); }, 5000);
      }
      return null;
    } finally {
      setReady(true);
    }
  }, []);
  refreshRef.current = refresh;

  useEffect(() => {
    // Skip the /me check while returning from Google OAuth — AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) { setReady(true); return; }
    refresh();
  }, [refresh]);

  // ONE ACCOUNT / ONE DEVICE: server revoked this session (account opened on another device),
  // or the session expired. The api interceptor already cleared the token — reflect it in the UI.
  useEffect(() => {
    const onRevoked = (e) => {
      setUser(null);
      if (e.detail === "SESSION_REVOKED") {
        toast.error("You've been signed out because your account was opened on another device.", { duration: 7000 });
      }
      window.dispatchEvent(new Event("auth-changed"));
    };
    window.addEventListener("session-revoked", onRevoked);
    return () => window.removeEventListener("session-revoked", onRevoked);
  }, []);

  // ONE DEVICE — active heartbeat: while signed in, re-validate the session every 15s AND whenever
  // the tab/window regains focus. If the account was opened on another device the server returns
  // 401 (the interceptor fires 'session-revoked'), so THIS device is booted within seconds even
  // while just sitting on a Live TV / Radio / VOD player. A manual refresh cannot restore access.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const check = () => { if (alive && localStorage.getItem(TOKEN_KEY)) endpoints.session().catch(() => {}); };
    const iv = setInterval(check, 15000);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", onVis); };
  }, [user]);

  const login = useCallback((token, u) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(u);
    window.dispatchEvent(new Event("auth-changed"));
  }, []);

  const logout = useCallback(() => {
    endpoints.logout().catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    window.dispatchEvent(new Event("auth-changed"));
  }, []);

  // Phone + OTP
  const startOtp = useCallback(async (payload) => {
    const { data } = await endpoints.otpRequest(payload);
    return data;
  }, []);

  const verifyOtp = useCallback(async (payload) => {
    const { data } = await endpoints.otpVerify(payload);
    login(data.accessToken, data.user);
    return data.user;
  }, [login]);

  // Emergent Google OAuth
  const googleSession = useCallback(async (sessionId) => {
    const { data } = await endpoints.googleSession(sessionId);
    login(data.accessToken, data.user);
    return data.user;
  }, [login]);

  // Staff username + password → OTP (SMS + email) → session
  const staffLogin = useCallback(async (username, password) => {
    const { data } = await endpoints.staffLogin({ username, password });
    return data; // { otpRequired, userId, sentSms, sentEmail, maskedPhone, maskedEmail }
  }, []);

  const staffVerifyOtp = useCallback(async (userId, code) => {
    const { data } = await endpoints.staffVerifyOtp({ userId, code });
    login(data.accessToken, data.user);
    return data.user;
  }, [login]);

  const value = {
    user, ready, loading: !ready,
    login, logout, refresh, startOtp, verifyOtp, googleSession, staffLogin, staffVerifyOtp,
    isAuthed: !!user,
    isMainAdmin: user?.role === "admin",
    isStaff: user?.role === "staff",
    isAdminArea: user?.role === "admin" || user?.role === "staff",
    isAdmin: user?.role === "admin",
    can: (module, action) => {
      if (!user) return false;
      if (user.role === "admin") return true;
      if (user.role !== "staff" || user.active === false) return false;
      return (user.permissions?.[module] || []).includes(action);
    },
    isSubscribed: !!user && ((user.tier && user.tier !== "free" &&
      (!user.subscriptionExpiresAt || new Date(user.subscriptionExpiresAt) > new Date())) ||
      (!!user.utilityAccessUntil && new Date(user.utilityAccessUntil) > new Date())),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
